import { useContext, useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import { Grid, Typography, Paper, CircularProgress, Box } from '@mui/material';
import ToastContext from 'src/store/toast-context';
import Footer from 'src/components/layout/Footer';
import 'src/styles/Login.scss';
import Header from 'src/components/page/Header';
import { useFilingReview } from 'src/services/pdf';
import AuthContext from 'src/store/auth-context';
import { useLoaded } from 'src/store/load-context';
import { useAPI } from 'src/services/api';
import BackTaxWarning from 'src/components/BackTaxWarning';
import { ReactComponent as PdfIcon } from 'src/assets/icons/PdfIcon.svg';
import { ReactComponent as AssetsIcon } from 'src/assets/icons/AssetsIcon.svg';
import { colors } from 'src/styles/theme';

const CompletePage = () => {
  const { handleError } = useContext(ToastContext);
  const filingPDF = useFilingReview();
  const { account, logout } = useContext(AuthContext);
  const [loadedPDF, setLoadedPDF] = useState(true);
  const [loadedAssetPDF, setLoadedAssetPDF] = useState(true);
  const { setLoaded } = useLoaded();
  const api = useAPI();
  const [backTaxBalance, setBackTaxBalance] = useState(null);

  const handleClick = async () => {
    try {
      window.location.replace(`https://www.utahcounty.gov/Dept/Assess/index.asp`);
    } catch (err) {
      console.error(err);
      handleError(err);
    }
  };

  useEffect(() => {
    setLoaded(true);

    const fetchBackTaxBalance = async () => {
      try {
        const { data } = await api.get('/account/backTaxBalance');
        if (data.balanceDue && data.balanceDue > 0) {
          setBackTaxBalance(data.balanceDue);
        }
      } catch (err) {
        console.error('Error fetching back tax balance:', err);
      }
    };

    fetchBackTaxBalance();
  }, [setLoaded, api]);

  return (
    <Grid
      className="main-container"
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Header onLogout={logout} />
      <Paper
        sx={{
          m: 'auto',
          padding: { xs: '1.5rem', sm: '2.5rem' },
          width: {
            lg: '45%',
            md: '65%',
            sm: '80%',
            xs: '95%',
          },
          maxWidth: '648px',
          textAlign: 'center',
          marginTop: { xs: '1.5rem', sm: '3rem' },
          marginBottom: backTaxBalance ? '1rem' : { xs: '1.5rem', sm: '3rem' },
          boxShadow: `0px 10px 15px 0px ${colors.backdrop.shadow}`,
          borderRadius: '4px',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: colors.grey[600],
            mb: 1,
          }}
        >
          Your tax filing was submitted successfully.
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.grey[600],
            mb: 3,
          }}
        >
          We&apos;ll notify you when this year&apos;s balance is available.
        </Typography>

        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: colors.grey[600],
            mb: 1,
          }}
        >
          Download your submission
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            mb: 4,
          }}
        >
          <Button
            data-cy="download-pdf"
            disabled={!loadedPDF}
            onClick={() =>
              filingPDF.downloadReview(
                {
                  accountNo: account.accountNo,
                  taxYear: account.taxYear,
                },
                setLoadedPDF
              )
            }
            startIcon={
              loadedPDF ? (
                <PdfIcon style={{ color: colors.secondary.main, width: 20, height: 20 }} />
              ) : (
                <CircularProgress size={16} />
              )
            }
            sx={{
              color: colors.secondary.main,
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            PDF
          </Button>
          <Button
            disabled={!loadedAssetPDF}
            onClick={() =>
              filingPDF.downloadAssets(
                {
                  accountNo: account.accountNo,
                  taxYear: account.taxYear,
                },
                setLoadedAssetPDF
              )
            }
            startIcon={
              loadedAssetPDF ? (
                <AssetsIcon style={{ color: colors.secondary.main, width: 20, height: 20 }} />
              ) : (
                <CircularProgress size={16} />
              )
            }
            sx={{
              color: colors.secondary.main,
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            ASSETS
          </Button>
        </Box>

        <Button
          variant="outlined"
          onClick={handleClick}
          sx={{
            width: '250px',
            height: '45px',
            borderColor: `${colors.primary.main}80`,
            borderWidth: '2px',
            color: colors.primary.main,
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            '&:hover': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
              backgroundColor: `${colors.primary.main}0A`,
            },
          }}
        >
          Return To Homepage
        </Button>
      </Paper>

      {backTaxBalance && (
        <Box
          sx={{
            width: {
              lg: '45%',
              md: '65%',
              sm: '80%',
              xs: '95%',
            },
            maxWidth: '648px',
            marginBottom: { xs: '1.5rem', sm: '3rem' },
          }}
        >
          <BackTaxWarning balanceDue={backTaxBalance} />
        </Box>
      )}
      <Footer
        email="melissasm@utahcounty.gov"
        phone="(801) 851-8255"
        address="100 East Center Street Suite 1200 Provo, Utah 84606"
        hours="8:00 am - 5:00 pm"
      />
    </Grid>
  );
};

export default CompletePage;
